import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiCamera, mdiClose, mdiPaperclip, mdiSend } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { FileDropOverlay } from '../shared/FileDropOverlay'
import { VoiceNoteRecorder, isRecordingSupported } from '../shared/VoiceNoteRecorder'
import { useCoarsePointer, useSendOnEnter } from '../../hooks/use-send-on-enter'
import { useGlobalFileDrop } from '../../hooks/use-global-file-drop'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * The ONE composer every external door renders (spec 2026-09-18-03).
 *
 * Before this component there were three: the resident thread's hand-rolled
 * footer, the QR page's voice-plus-photos form, and the printed address's bare
 * textarea. Which one a resident got depended on the paper in their hand, and
 * only the first of the three could carry a photo AND a sentence. So it was
 * lifted out whole — text, files, camera, voice, the page-wide drop target, the
 * pending chips and the three client-side limits — and the other two doors were
 * pointed at it. There is one composer to maintain now, which is the whole
 * point of the spec.
 *
 * Two contracts shape the API:
 *
 *   - `onSend` returns a promise. Resolving clears the composer; REJECTING
 *     keeps every part of it, the recording above all. A resident who was rate
 *     limited must not have to record their voice note a second time — that is
 *     the one failure that makes them give up.
 *   - Nothing is persisted by this component and nothing is persisted before
 *     `onSend` resolves. On the two doors where no thread exists yet, the send
 *     is what creates it: a scan that goes nowhere leaves no row.
 *
 * The four affordances — write, photograph, attach, record — sit on ONE row at
 * thumb height and are the same size, because this is read on a phone, at
 * arm's length, by someone who has never seen the page before. Camera capture
 * is its own button (`capture="environment"` opens the rear camera straight
 * away) rather than a corner of the file picker.
 */

/** What a send carries. Identical on all three doors. */
export interface ResidentComposerPayload {
    body: string
    files: File[]
    voice: Blob | null
}

export interface ResidentComposerProps {
    /** Sends the message. Resolve to clear the composer, reject to keep it. */
    onSend: (payload: ResidentComposerPayload) => Promise<unknown>
    sending: boolean
    /** The server's own refusal, rendered in the composer's error slot. */
    error?: string | null
    /** A notice rendered above the box — the closed-request hint on `/c/`. */
    notice?: ReactNode
    placeholder?: string
    /** Label of the send button. Defaults to the resident page's "Send". */
    sendLabel?: string
    /** Prefix of the test ids this composer renders. */
    testIdPrefix?: string
    /** Overrides for the two ids that predate this component on `/c/`. */
    bodyTestId?: string
    errorTestId?: string
    /** Prefix handed to the recorder, which has ids of its own. */
    voiceTestIdPrefix?: string
}

/**
 * The server's limits, mirrored here so a resident is told before their upload
 * travels — never INSTEAD of the server's, which is where they are enforced
 * (spec 2026-09-09-01 § 1).
 */
const MAX_FILES = 10
const MAX_FILE_BYTES = 25 * 1024 * 1024
const MAX_VOICE_BYTES = 12 * 1024 * 1024

export function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function ResidentComposer({
    onSend,
    sending,
    error,
    notice,
    placeholder,
    sendLabel,
    testIdPrefix = 'resident',
    bodyTestId,
    errorTestId,
    voiceTestIdPrefix,
}: ResidentComposerProps) {
    const { t } = useTranslation('conversations')
    const [draft, setDraft] = useState('')
    const [pending, setPending] = useState<File[]>([])
    const [voice, setVoice] = useState<Blob | null>(null)
    const [localError, setLocalError] = useState<string | null>(null)
    const [voiceNotice, setVoiceNotice] = useState('')
    const [recording, setRecording] = useState(false)
    const bodyRef = useRef<HTMLTextAreaElement | null>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const cameraInputRef = useRef<HTMLInputElement | null>(null)

    /**
     * Take files from a picker, the camera or a drop, with the server's own
     * limits checked first so a resident is told now rather than after the
     * upload.
     */
    const addFiles = useCallback((incoming: File[]) => {
        if (incoming.length === 0) return
        setLocalError(null)
        setPending((current) => {
            const next = [...current]
            for (const file of incoming) {
                if (next.length >= MAX_FILES) {
                    setLocalError(t('public.too_many_files', { count: MAX_FILES }))
                    break
                }
                if (file.size > MAX_FILE_BYTES) {
                    setLocalError(t('public.file_too_large', { name: file.name, limit: formatBytes(MAX_FILE_BYTES) }))
                    continue
                }
                next.push(file)
            }

            return next
        })
    }, [t])

    const removeFile = useCallback((index: number) => {
        setPending((current) => current.filter((_, i) => i !== index))
    }, [])

    /**
     * The recording is capped like any other part.
     *
     * The 2-minute recorder cap bounds it only indirectly — a high-bitrate
     * two-minute take can still cross 12 MiB — and the server would answer 422
     * after the upload travelled. Refuse it here, in the same error slot the
     * file limits use, and keep nothing: the resident re-records shorter.
     */
    const handleVoiceChange = useCallback((blob: Blob | null) => {
        if (!blob) setVoiceNotice('')
        if (blob && blob.size > MAX_VOICE_BYTES) {
            setLocalError(t('public.file_too_large', {
                name: t('public.voice_message'),
                limit: formatBytes(MAX_VOICE_BYTES),
            }))
            setVoice(null)

            return
        }
        setLocalError(null)
        setVoice(blob)
    }, [t])

    // The PAGE is the drop target, not the composer: a file dragged onto this
    // page can only be meant for this message.
    const { isDragging } = useGlobalFileDrop(addFiles, !sending)

    // A message is sendable when there is text, OR a file, OR a recording: a
    // photo of the leak with no caption says plenty.
    const hasContent = draft.trim() !== '' || pending.length > 0 || voice !== null
    const canSend = !sending && hasContent

    const submitDraft = useCallback(() => {
        if (sending) return
        if (draft.trim() === '' && pending.length === 0 && !voice) return
        // What is being sent is captured HERE, and a send that comes back
        // clears exactly that and nothing else.
        //
        // A resident types the next line, or attaches the next photo, while
        // the previous send is still travelling — the message they just sent
        // is already on screen by then, because the server's answer is written
        // into the cache before this promise resolves. Clearing the LIVE state
        // wholesale therefore ate whatever they had added in that window, with
        // no error and no trace. The window is small on a desk and wide on a
        // phone in a stairwell.
        const sentBody = draft
        const sentFiles = pending
        const sentVoice = voice
        void (async () => {
            try {
                await onSend({ body: sentBody, files: sentFiles, voice: sentVoice })
                setDraft((current) => (current === sentBody ? '' : current))
                setPending((current) => current.filter((file) => !sentFiles.includes(file)))
                setVoice((current) => (current === sentVoice ? null : current))
                setVoiceNotice('')
                setLocalError(null)
            } catch {
                // Point 6 of spec 2026-09-18-03: a refused send costs nothing.
                // The text, the photos and above all the recording stay exactly
                // where they were, and the error renders next to them.
            }
        })()
    }, [draft, pending, voice, sending, onSend])

    const handleBodyKeyDown = useSendOnEnter({ send: submitDraft, enabled: canSend })
    const isCoarsePointer = useCoarsePointer()

    // Probed once: an insecure origin or a browser without MediaRecorder must
    // HIDE the microphone, not offer a button that cannot work.
    const [canRecord] = useState(() => isRecordingSupported())

    // The box grows with what is written in it, up to the cap the class sets:
    // a single line at rest keeps the four affordances on one row at thumb
    // height, and a paragraph is still readable while it is being written.
    useEffect(() => {
        const el = bodyRef.current
        if (!el) return
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
    }, [draft])

    const shown = localError ?? error ?? null

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-composer`}>
            {isDragging && <FileDropOverlay label={t('public.drop_files')} testId={`${testIdPrefix}-drop-overlay`} />}
            {notice}
            {pending.length > 0 && (
                <ul
                    className="flex flex-wrap gap-2"
                    aria-label={t('public.pending_files')}
                    data-testid={`${testIdPrefix}-pending-files`}
                >
                    {pending.map((file, index) => (
                        <li
                            key={`${file.name}-${index}`}
                            className="flex items-center gap-2 rounded-lg border bg-muted/40 px-2 py-1.5 text-xs max-w-full"
                            data-testid={`${testIdPrefix}-pending-file-${index}`}
                        >
                            <PendingThumbnail file={file} />
                            <span className="truncate max-w-[10rem] text-foreground">{file.name}</span>
                            <span className="text-muted-foreground flex-shrink-0">{formatBytes(file.size)}</span>
                            <button
                                type="button"
                                onClick={() => removeFile(index)}
                                aria-label={t('public.remove_file')}
                                title={t('public.remove_file')}
                                className="text-muted-foreground hover:text-foreground flex-shrink-0"
                                data-testid={`${testIdPrefix}-pending-file-remove-${index}`}
                            >
                                <AppIcon path={mdiClose} size="xs" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {voice && voiceNotice && (
                <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-voice-notice`}>
                    {voiceNotice}
                </p>
            )}
            {shown && (
                <p className="text-xs text-destructive" data-testid={errorTestId ?? `${testIdPrefix}-error`}>
                    {shown}
                </p>
            )}

            {/*
              * No `accept` on the file picker: the server's policy
              * (documents.AcceptUpload) is a DENYLIST of dangerous types, so
              * there is no positive list of extensions to derive one from — an
              * invented allow-list would hide files the server accepts. The
              * camera input is the opposite case: it exists precisely to ask a
              * phone for its rear camera rather than a file browser.
              */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                    addFiles(Array.from(e.target.files ?? []))
                    // Reset so picking the same file twice still fires.
                    e.target.value = ''
                }}
                data-testid={`${testIdPrefix}-file-input`}
            />
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={(e) => {
                    addFiles(Array.from(e.target.files ?? []))
                    e.target.value = ''
                }}
                data-testid={`${testIdPrefix}-camera-input`}
            />

            {/*
              * A recorded note is played back on its own full-width line, the
              * way WhatsApp holds a take before it is sent: the round button on
              * the right is then the send.
              */}
            {canRecord && voice && (
                <VoiceNoteRecorder
                    value={voice}
                    onChange={handleVoiceChange}
                    disabled={sending}
                    compact
                    testIdPrefix={voiceTestIdPrefix ?? testIdPrefix}
                />
            )}

            <div className="flex items-end gap-2">
                <div
                    className="flex min-w-0 flex-1 items-end rounded-3xl border border-input bg-background pl-1 pr-0.5 shadow-sm focus-within:ring-1 focus-within:ring-ring"
                    data-testid={`${testIdPrefix}-input-pill`}
                >
                    <Textarea
                        ref={bodyRef}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={handleBodyKeyDown}
                        placeholder={placeholder ?? t('public.placeholder')}
                        aria-label={placeholder ?? t('public.placeholder')}
                        rows={1}
                        maxLength={4000}
                        // 16px, not 14: iOS zooms the page into any field
                        // smaller than that the moment it is focused.
                        className="min-h-11 max-h-40 flex-1 resize-none border-0 bg-transparent px-3 py-2.5 text-base shadow-none focus-visible:ring-0"
                        data-testid={bodyTestId ?? `${testIdPrefix}-body`}
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-11 w-10 flex-shrink-0 rounded-full text-muted-foreground"
                        disabled={sending}
                        onClick={() => fileInputRef.current?.click()}
                        aria-label={t('public.attach')}
                        title={t('public.attach')}
                        data-testid={`${testIdPrefix}-attach`}
                    >
                        <AppIcon path={mdiPaperclip} size="md" />
                    </Button>
                    {/* The camera steps aside while text is being written, so
                        the line keeps its width for the words. */}
                    {draft === '' && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-11 w-10 flex-shrink-0 rounded-full text-muted-foreground"
                            disabled={sending}
                            onClick={() => cameraInputRef.current?.click()}
                            aria-label={t('composer.camera')}
                            title={t('composer.camera')}
                            data-testid={`${testIdPrefix}-camera`}
                        >
                            <AppIcon path={mdiCamera} size="md" />
                        </Button>
                    )}
                </div>
                {/*
                  * ONE round button, as on WhatsApp: the microphone while there
                  * is nothing to send, the send as soon as there is. A take in
                  * progress keeps the microphone (its stop) whatever is typed
                  * meanwhile, so the recording is never unmounted mid-way.
                  */}
                {canRecord && !voice && (recording || !hasContent) ? (
                    <VoiceNoteRecorder
                        value={null}
                        onChange={handleVoiceChange}
                        // No onRecorded: the transcript is never client-supplied
                        // on these surfaces. The recording travels as it is and
                        // the `message_transcription` job writes the text onto
                        // the very same message afterwards.
                        onNoSpeech={() => setVoiceNotice(t('composer.voice_no_speech'))}
                        onRecordingChange={setRecording}
                        disabled={sending}
                        compact
                        idleVariant="default"
                        buttonClassName="rounded-full"
                        testIdPrefix={voiceTestIdPrefix ?? testIdPrefix}
                    />
                ) : (
                    <Button
                        type="button"
                        size="icon"
                        className="h-11 w-11 flex-shrink-0 rounded-full"
                        disabled={!canSend}
                        onClick={submitDraft}
                        aria-label={sendLabel ?? t('public.send')}
                        title={sendLabel ?? t('public.send')}
                        data-testid={`${testIdPrefix}-send`}
                    >
                        <AppIcon path={mdiSend} size="md" />
                    </Button>
                )}
            </div>
            {!isCoarsePointer && (
                <p className="text-right text-xs text-muted-foreground" data-testid={`${testIdPrefix}-composer-hint`}>
                    {t('composer.hint')}
                </p>
            )}
        </div>
    )
}

/** A thumbnail of a pending image, so a resident can see WHICH photo they
 * picked before it travels. The object URL is revoked with the chip. */
function PendingThumbnail({ file }: { file: File }) {
    const [url, setUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!file.type.startsWith('image/')) {
            setUrl(null)

            return
        }
        const objectUrl = URL.createObjectURL(file)
        setUrl(objectUrl)

        return () => URL.revokeObjectURL(objectUrl)
    }, [file])

    if (!url) return <AppIcon path={SECTION_ICONS.documents} size="sm" className="flex-shrink-0" />

    return <img src={url} alt="" className="h-8 w-8 rounded object-cover flex-shrink-0" />
}
